import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BehaviorSubject, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { getApiUrl } from '@shared/utils/app-util';

export interface NotificationItem {
  id: number;
  title: string;
  description: string;
  created_at: string;
  is_read: boolean;
  read_at: string | null;
}

export interface NotificationsResponseMeta {
  total: number; page: number; page_size: number; total_pages: number; count: number;
}

export interface NotificationsResponse {
  message: string;
  notifications: NotificationItem[];
  meta: NotificationsResponseMeta;
  unread_count: number;
}

export interface UnreadCountResponse { unread_count: number; }

@Injectable({ providedIn: 'root' })
export class NotificationService {
  constructor(private readonly http: HttpClient) {}

  private unreadCountSubject = new BehaviorSubject<number>(0);
  unreadCount$ = this.unreadCountSubject.asObservable();

  // Cross-app event: request opening the notification popup
  openPopup$ = new Subject<void>();
  requestOpenPopup(): void { this.openPopup$.next(); }

  refreshUnreadCount(): Observable<UnreadCountResponse> {
    const url = getApiUrl('NOTIFICATIONS_UNREAD_COUNT');
    return this.http.get<UnreadCountResponse>(url).pipe(
      tap(res => this.unreadCountSubject.next(res?.unread_count ?? 0))
    );
  }

  getUnreadCount(): Observable<UnreadCountResponse> {
    const url = getApiUrl('NOTIFICATIONS_UNREAD_COUNT');
    return this.http.get<UnreadCountResponse>(url).pipe(
      tap(res => this.unreadCountSubject.next(res?.unread_count ?? 0))
    );
  }

  getNotifications(page = 1, pageSize = 10, unreadOnly = true): Observable<NotificationsResponse> {
    const url = getApiUrl('NOTIFICATIONS');
    const params = new HttpParams()
      .set('page', String(page))
      .set('page_size', String(pageSize))
      .set('unread_only', String(unreadOnly));
    return this.http.get<NotificationsResponse>(url, { params }).pipe(
      tap(res => {
        if (typeof res?.unread_count === 'number') this.unreadCountSubject.next(res.unread_count);
      })
    );
  }

  markRead(id: number): Observable<{ message: string; success: boolean; }>
  {
    const base = getApiUrl('NOTIFICATIONS'); // ends with '/'
    const url = `${base}${id}/read`;
    return this.http.patch<{ message: string; success: boolean; }>(url, {}).pipe(
      tap(() => this.refreshUnreadCount().subscribe())
    );
  }

  markAllRead(): Observable<{ message: string; success: boolean }> {
    const url = getApiUrl('NOTIFICATIONS_MARK_ALL_READ');
    return this.http.patch<{ message: string; success: boolean }>(url, {}).pipe(
      tap(() => this.refreshUnreadCount().subscribe())
    );
  }
}
